import LoadingQuestion from 'components/LoadingComponents/LoadingQuestion';
import {useEffect, useState} from 'react';
import {Button, TextArea} from 'letrus-ui';
import {EnrichedQuestion} from 'utils/types/questionnaires';
import {getOpenQuestionFeedbackCopy} from 'containers/NewQuestionnaire/hooks/useQuestion/openQuestionFeedbackCopy';
import {QuestionSubmitParams} from 'containers/PracticeTest';
import styles from './TextQuestion.module.scss';

export interface TextQuestionProps {
  question: EnrichedQuestion;
  onGoBack(): void;
  goToNextQuestion(): void;
  onQuestionSubmit(params: QuestionSubmitParams): void;
  isLoading: boolean;
  isQuestionnaireFinished: boolean;
  answersVisible?: boolean;
}

function TextQuestion({
  question,
  onGoBack,
  onQuestionSubmit,
  goToNextQuestion,
  isLoading,
  isQuestionnaireFinished,
  answersVisible = false,
}: TextQuestionProps): JSX.Element {
  const [answer, setAnswer] = useState('');

  // Open-text questions carry the AI evaluator's score in open_question_feedback; map it to the
  // shared student-facing copy (same helper the NewQuestionnaire flow uses) and fall back to the
  // static field when there is no AI score yet.
  const generalFeedback =
    getOpenQuestionFeedbackCopy(question.open_question_feedback?.score) ||
    question.correct_feedback ||
    question.incorrect_feedback;

  useEffect(() => {
    setAnswer(question.answer ?? '');
  }, [question]);

  function handleSubmit() {
    onQuestionSubmit({
      questionId: question.id,
      answer,
      responses: [],
    });

    goToNextQuestion();
  }

  return isLoading ? (
    <LoadingQuestion questionType="text" />
  ) : (
    <article className={styles.wrapper}>
      <div className={styles.header}>
        <h1>{question.name}</h1>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{__html: question.text || ''}} />
      </div>

      <TextArea
        maxLength={2000}
        value={answer}
        placeholder="Resposta"
        readOnly={isQuestionnaireFinished}
        label="Responda com até 2000 caracteres"
        onChange={(e) => setAnswer(e.target.value)}
        data-testid="text-input"
      />

      {answersVisible && generalFeedback && (
        <span className={styles.feedback}>{generalFeedback}</span>
      )}

      <div className={styles.controls}>
        <Button
          type="button"
          variant="secondary"
          onClick={onGoBack}
          disabled={question.order === 1}
        >
          Voltar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={!answer.trim()}
          data-testid="submit-button"
        >
          {question.button_text || 'Próxima'}
        </Button>
      </div>
    </article>
  );
}

export default TextQuestion;
